import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { config } from '../../config';
import './CLIAuth.scss';

const API_URL = config.API_URI;

const AVAILABLE_SCOPES = [
  { id: 'boards:read', label: 'Read boards', category: 'Boards' },
  { id: 'boards:write', label: 'Create/update boards', category: 'Boards' },
  { id: 'cards:read', label: 'Read cards', category: 'Cards' },
  { id: 'cards:write', label: 'Create/update cards', category: 'Cards' },
  { id: 'lists:read', label: 'Read lists', category: 'Lists' },
  { id: 'lists:write', label: 'Create/update lists', category: 'Lists' },
  { id: 'pages:read', label: 'Read pages', category: 'Pages' },
  { id: 'pages:write', label: 'Create/update pages', category: 'Pages' },
  { id: 'workspaces:read', label: 'Read workspaces', category: 'Workspaces' },
  { id: 'workspaces:write', label: 'Create/update workspaces', category: 'Workspaces' },
  { id: 'members:read', label: 'Read members', category: 'Members' },
  { id: 'ai:read', label: 'Read AI data', category: 'AI' },
  { id: 'ai:write', label: 'Use AI features', category: 'AI' },
  { id: 'analytics:read', label: 'Read analytics', category: 'Analytics' },
  { id: 'webhooks:read', label: 'Read webhooks', category: 'Webhooks' },
  { id: 'webhooks:write', label: 'Manage webhooks', category: 'Webhooks' },
];

const DEFAULT_CLI_SCOPES = [
  'boards:read', 'boards:write', 'cards:read', 'cards:write',
  'lists:read', 'lists:write', 'pages:read', 'workspaces:read',
  'ai:read', 'ai:write',
];

const CLIAuth = () => {
  const user = useSelector(state => state.user);
  const { workspaces } = useSelector(state => state.workspaces);
  const [searchParams] = useSearchParams();

  const cliState = searchParams.get('state');
  const rawPort = searchParams.get('port');
  const cliPort = (() => {
    const p = parseInt(rawPort, 10);
    return (!isNaN(p) && p >= 1024 && p <= 65535) ? p : null;
  })();
  const isCLICallback = Boolean(cliState && cliPort);

  const [step, setStep] = useState('configure');
  const [keyName, setKeyName] = useState('CLI Access Key');
  const [selectedWorkspace, setSelectedWorkspace] = useState('');
  const [selectedScopes, setSelectedScopes] = useState(DEFAULT_CLI_SCOPES);
  const [generatedKey, setGeneratedKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cliWorkspaceId, setCliWorkspaceId] = useState('');
  const [copied, setCopied] = useState(false);

  // CLI callback: silently fetch first workspace for API key creation
  useEffect(() => {
    if (!isCLICallback) return;
    (async () => {
      try {
        const wsRes = await fetch(`${API_URL}/v1/workspaces/me`, { credentials: 'include' });
        const wsData = await wsRes.json();
        if (!wsRes.ok) return;
        const wsList = Array.isArray(wsData) ? wsData : (wsData.workspaces || wsData.data || []);
        if (wsList.length > 0) {
          setCliWorkspaceId(wsList[0].shortId);
        }
      } catch { /* ignore */ }
    })();
  }, [isCLICallback]);

  // For manual mode: auto-select workspace if only one exists
  useEffect(() => {
    if (isCLICallback) return;
    const wsList = Array.isArray(workspaces) ? workspaces : [];
    if (wsList.length === 1 && !selectedWorkspace) {
      setSelectedWorkspace(wsList[0].shortId);
    }
  }, [workspaces, selectedWorkspace, isCLICallback]);

  const toggleScope = useCallback((scopeId) => {
    setSelectedScopes(prev =>
      prev.includes(scopeId)
        ? prev.filter(s => s !== scopeId)
        : [...prev, scopeId]
    );
  }, []);

  const selectAll = useCallback(() => {
    setSelectedScopes(AVAILABLE_SCOPES.map(s => s.id));
  }, []);

  const handleCliAuthorize = async () => {
    if (selectedScopes.length === 0) {
      setError('Please select at least one permission');
      return;
    }

    const wsId = cliWorkspaceId;
    if (!wsId) {
      setError('No workspace found. Please create a workspace first.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/v1/api-keys`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: 'CLI Access Key',
          workspaceId: wsId,
          scopes: selectedScopes,
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create API key');

      const key = data.data.key;
      setStep('redirecting');
      const callbackUrl = `http://127.0.0.1:${cliPort}/callback?token=${encodeURIComponent(key)}&state=${encodeURIComponent(cliState)}`;
      setTimeout(() => { window.location.href = callbackUrl; }, 1200);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    window.close();
  };

  const handleCreateKey = async () => {
    if (!selectedWorkspace) {
      setError('Please select a workspace');
      return;
    }
    if (selectedScopes.length === 0) {
      setError('Please select at least one scope');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/v1/api-keys`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: keyName,
          workspaceId: selectedWorkspace,
          scopes: selectedScopes,
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create API key');

      setGeneratedKey(data.data.key);
      setStep('created');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyKey = () => {
    navigator.clipboard.writeText(generatedKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyCLICommand = () => {
    navigator.clipboard.writeText(`zoobbe auth login --token ${generatedKey}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const scopesByCategory = useMemo(() => AVAILABLE_SCOPES.reduce((acc, scope) => {
    if (!acc[scope.category]) acc[scope.category] = [];
    acc[scope.category].push(scope);
    return acc;
  }, {}), []);

  // ── CLI Callback Mode ──
  if (isCLICallback) {
    return (
      <div className="cli-auth-container">
        <div className="cli-auth-card">
          {step === 'redirecting' ? (
            <div className="cli-auth-status">
              <div className="cli-auth-success-icon">
                <span className="material-symbols-outlined">check_circle</span>
              </div>
              <h1 className="cli-auth-title">CLI Authorized</h1>
              <p className="cli-auth-subtitle">Sending credentials to your terminal...</p>
              <p className="cli-auth-hint">You can close this tab after the CLI confirms login.</p>
            </div>
          ) : (
            <>
              {/* Banner */}
              <div className="cli-auth-banner">
                <div className="cli-auth-app-icon">
                  <span className="material-symbols-outlined">terminal</span>
                </div>
                <div className="cli-auth-app-info">
                  <p className="cli-auth-app-name">Zoobbe CLI</p>
                  <p className="cli-auth-app-desc">wants access to your account</p>
                </div>
              </div>

              {/* Body */}
              <div className="cli-auth-body">
                <div className="cli-auth-header">
                  <h1 className="cli-auth-title">Review permissions</h1>
                  <p className="cli-auth-subtitle">
                    This will allow <strong>Zoobbe CLI</strong> to access your data with the selected permissions.
                  </p>
                </div>

                <div className="cli-auth-permissions">
                  <div className="cli-auth-permissions-header">
                    <span className="cli-auth-permissions-label">Permissions</span>
                    <button onClick={selectAll} className="cli-auth-select-all-btn">Select all</button>
                  </div>
                  <div className="cli-auth-permissions-list">
                    {Object.entries(scopesByCategory).map(([category, scopes]) => (
                      <div key={category} className="cli-auth-perm-group">
                        <div className="cli-auth-perm-category">{category}</div>
                        {scopes.map(scope => (
                          <label key={scope.id} className="cli-auth-perm-item">
                            <input
                              type="checkbox"
                              checked={selectedScopes.includes(scope.id)}
                              onChange={() => toggleScope(scope.id)}
                            />
                            <span>{scope.label}</span>
                          </label>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>

                {error && <div className="cli-auth-error">{error}</div>}
              </div>

              {/* Footer */}
              <div className="cli-auth-footer">
                <div className="cli-auth-actions">
                  <button onClick={handleCancel} className="cli-auth-button-cancel">
                    Cancel
                  </button>
                  <button
                    onClick={handleCliAuthorize}
                    disabled={loading || !cliWorkspaceId}
                    className="cli-auth-button"
                  >
                    {loading ? 'Authorizing...' : 'Authorize'}
                  </button>
                </div>
                <p className="cli-auth-user-info">
                  Signed in as <strong>{user?.name || user?.email}</strong>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── Manual API Key Mode ──
  return (
    <div className="cli-auth-container">
      <div className="cli-auth-card" style={{ maxWidth: 600 }}>
        <div className="cli-auth-body">
          {step === 'configure' && (
            <>
              <div className="cli-auth-header">
                <h1 className="cli-auth-title">Zoobbe CLI & API Access</h1>
                <p className="cli-auth-subtitle">
                  Generate an API key to use with the Zoobbe CLI, MCP server, or third-party integrations.
                </p>
              </div>

              <div className="cli-auth-form">
                <div className="cli-auth-field">
                  <label className="cli-auth-label">Key Name</label>
                  <input
                    type="text"
                    value={keyName}
                    onChange={(e) => setKeyName(e.target.value)}
                    className="cli-auth-input"
                    placeholder="e.g., CLI Access Key"
                  />
                </div>

                <div className="cli-auth-field">
                  <label className="cli-auth-label">Workspace</label>
                  <select
                    value={selectedWorkspace}
                    onChange={(e) => setSelectedWorkspace(e.target.value)}
                    className="cli-auth-select"
                  >
                    <option value="">Select a workspace...</option>
                    {(Array.isArray(workspaces) ? workspaces : []).map(ws => (
                      <option key={ws.shortId || ws._id} value={ws.shortId}>
                        {ws.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="cli-auth-field">
                  <div className="cli-auth-scope-header">
                    <label className="cli-auth-label">Permissions</label>
                    <button onClick={selectAll} className="cli-auth-select-all-btn">Select All</button>
                  </div>
                  <div className="cli-auth-scope-grid">
                    {Object.entries(scopesByCategory).map(([category, scopes]) => (
                      <div key={category} className="cli-auth-scope-category">
                        <div className="cli-auth-category-name">{category}</div>
                        {scopes.map(scope => (
                          <label key={scope.id} className="cli-auth-scope-item">
                            <input
                              type="checkbox"
                              checked={selectedScopes.includes(scope.id)}
                              onChange={() => toggleScope(scope.id)}
                            />
                            <span>{scope.label}</span>
                          </label>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>

                {error && <div className="cli-auth-error">{error}</div>}

                <button
                  onClick={handleCreateKey}
                  disabled={loading}
                  className="cli-auth-button"
                >
                  {loading ? 'Creating...' : 'Create API Key'}
                </button>
              </div>
            </>
          )}

          {step === 'created' && (
            <div className="cli-auth-result">
              <div className="cli-auth-header">
                <h1 className="cli-auth-title">API Key Created</h1>
                <p className="cli-auth-subtitle">Save this key now — you won't be able to see it again.</p>
              </div>

              <div className="cli-auth-key-display">
                <code className="cli-auth-key-code">{generatedKey}</code>
                <button onClick={copyKey} className="cli-auth-copy-btn">
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>

              <div className="cli-auth-instructions">
                <h3 className="cli-auth-instruction-title">Quick Start</h3>

                <div className="cli-auth-instruction-block">
                  <strong>CLI Login:</strong>
                  <div className="cli-auth-code-block">
                    <code>zoobbe auth login --token {generatedKey}</code>
                    <button onClick={copyCLICommand} className="cli-auth-copy-small-btn">Copy</button>
                  </div>
                </div>

                <div className="cli-auth-instruction-block">
                  <strong>MCP Server (Claude Code):</strong>
                  <div className="cli-auth-code-block">
                    <code>ZOOBBE_API_KEY={generatedKey} npx @zoobbe/mcp</code>
                  </div>
                </div>

                <div className="cli-auth-instruction-block">
                  <strong>API Usage:</strong>
                  <div className="cli-auth-code-block">
                    <code>{`curl -H "Authorization: Bearer ${generatedKey}" \\
  ${API_URL}/v1/boards`}</code>
                  </div>
                </div>
              </div>

              <button onClick={() => { setStep('configure'); setGeneratedKey(''); }} className="cli-auth-button-secondary">
                Create Another Key
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CLIAuth;
